import { MutableRefObject, useCallback, useEffect, useState } from 'react'
import { Highlight } from '../types/highlights'
import { useRouter } from 'next/router'
import { joinWithSlash } from 'utilities/links'
import { useProfile } from 'containers/profile/hooks/useProfile'
import { useHighlights } from './useHighlights'

export const useHighlightCardModal = (
  ishighlightPage?: boolean,
  higlightsListRef?: MutableRefObject<HTMLDivElement>
) => {
  const { highlights } = useHighlights()
  const { profile } = useProfile()
  const router = useRouter()
  const [highlightModalOpen, setHighlightModalOpen] = useState(false)
  const [selectedHighlight, setSelectedHighlight] = useState<Highlight | null>(
    null
  )
  const [firstLoadWithHighlights, setFirstLoadWithHighlights] = useState(false)

  const scrollToHighlightId = useCallback(
    (highlightId: string) => {
      const highlightCard = higlightsListRef?.current?.querySelector(
        `[data-highlight-id="${highlightId}"]`
      )

      if (highlightCard) {
        setTimeout(() => {
          highlightCard.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        })
      }
    },
    [higlightsListRef]
  )

  const openModal = useCallback(
    (highlight: Highlight, scrollToElement?: boolean) => {
      if (scrollToElement) {
        setTimeout(() => {
          scrollToHighlightId(highlight.id)
        }, 300)
      }
      setHighlightModalOpen(true)
      setTimeout(() => {
        setSelectedHighlight(highlight)
      }, 0)
      if (ishighlightPage && typeof window !== 'undefined') {
        window.history.pushState(
          {},
          '',
          joinWithSlash(profile?.usernameLower, 'highlights', highlight.id)
        )
      }
    },
    [scrollToHighlightId, ishighlightPage, profile?.usernameLower]
  )

  const closeModal = useCallback(() => {
    setHighlightModalOpen(false)
    setSelectedHighlight(null)
    if (ishighlightPage && typeof window !== 'undefined') {
      window.history.pushState(
        {},
        '',
        joinWithSlash(profile?.usernameLower, 'highlights')
      )
    }
  }, [profile?.usernameLower, ishighlightPage])

  useEffect(() => {
    if (
      router.query?.highlight_id &&
      highlights?.length > 0 &&
      !firstLoadWithHighlights &&
      higlightsListRef?.current
    ) {
      setFirstLoadWithHighlights(true)
      const highlight = highlights.find(
        (h) => h.id === router.query?.highlight_id
      )

      if (highlight && !selectedHighlight) {
        openModal(highlight, true)
      }
    }
  }, [
    firstLoadWithHighlights,
    highlights,
    openModal,
    router.query?.highlight_id,
    selectedHighlight,
    higlightsListRef,
  ])

  return {
    highlightModalOpen,
    selectedHighlight,
    openModal,
    closeModal,
  }
}
